import {
    createProductCategory,
    getProductCategories,
    type ProductCategoryItem,
} from '@/lib/api/product-category/product-category-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useAuth } from '@/store/hooks'
import { useEffect, useState } from 'react'
import { MdAdd } from 'react-icons/md'
import { toast } from 'react-toastify'

interface ProductCategorySelectorProps {
    label: string
    value: number | null
    onChange: (id: number | null) => void
    /**
     * Presente = seletor de SUBcategoria: só lista as filhas deste pai, e fica
     * desabilitado enquanto nenhuma categoria foi escolhida. Ausente = seletor de
     * categoria raiz.
     */
    parentId?: number | null
}

/**
 * Categoria do produto — a mesma que a LOJA usa no menu da vitrine, nas promoções e
 * nos cupons (`ProductCategoryItem`). Não confundir com `CatalogType`, que agrupa os
 * serviços: ter os dois é o que deixa explícito de onde vem cada item do menu.
 *
 * Cria categoria/subcategoria ali mesmo ("+ Nova"), para o cadastro do produto não
 * obrigar a sair do catálogo, ir à tela de categorias e voltar com o formulário
 * perdido. Só aparece para quem pode criar categoria.
 */
export default function ProductCategorySelector({
    label,
    value,
    onChange,
    parentId,
}: ProductCategorySelectorProps) {
    const { user } = useAuth()
    const { canCreate } = useResourcePermissions(
        'registrations',
        'product_categories'
    )
    const [categories, setCategories] = useState<ProductCategoryItem[]>([])
    const [loading, setLoading] = useState(false)
    const [creating, setCreating] = useState(false)
    const [newName, setNewName] = useState('')
    const [saving, setSaving] = useState(false)
    const isSubcategory = parentId !== undefined

    useEffect(() => {
        let active = true
        const load = async () => {
            setLoading(true)
            try {
                const res = await getProductCategories(user?.company?.id)
                if (active && res.success && res.result) {
                    setCategories(res.result)
                }
            } finally {
                if (active) setLoading(false)
            }
        }
        void load()
        return () => {
            active = false
        }
    }, [user?.company?.id])

    // Trocar a categoria fecha a criação de subcategoria aberta: ela seria criada
    // embaixo do pai anterior.
    useEffect(() => {
        if (isSubcategory) {
            setCreating(false)
            setNewName('')
        }
    }, [isSubcategory, parentId])

    const options = isSubcategory
        ? categories.filter(
              category => category.isSubcategory && category.parentId === parentId
          )
        : categories.filter(category => !category.isSubcategory)

    const disabled = loading || (isSubcategory && !parentId)
    const canCreateHere = canCreate && !!user?.company?.id && !disabled

    const closeCreate = () => {
        setCreating(false)
        setNewName('')
    }

    const handleCreate = async () => {
        const name = newName.trim()
        if (!name || !user?.company?.id) return

        // Nome que já existe neste nível: seleciona o existente em vez de duplicar.
        const existing = options.find(
            category => category.name.toLowerCase() === name.toLowerCase()
        )
        if (existing) {
            onChange(existing.id)
            closeCreate()
            toast.info(
                `${isSubcategory ? 'Subcategoria' : 'Categoria'} "${existing.name}" já existe e foi selecionada`
            )
            return
        }

        setSaving(true)
        try {
            const res = await createProductCategory({
                name,
                companyId: user.company.id,
                ...(isSubcategory && parentId
                    ? { isSubcategory: true, parentId }
                    : {}),
            })
            if (res.success && res.result) {
                const created = res.result
                setCategories(prev =>
                    [...prev, created].sort((a, b) =>
                        a.name.localeCompare(b.name)
                    )
                )
                onChange(created.id)
                closeCreate()
                toast.success(
                    isSubcategory
                        ? 'Subcategoria criada com sucesso'
                        : 'Categoria criada com sucesso'
                )
            } else {
                toast.error(
                    res.error ||
                        (isSubcategory
                            ? 'Erro ao criar subcategoria'
                            : 'Erro ao criar categoria')
                )
            }
        } catch {
            toast.error(
                isSubcategory
                    ? 'Erro ao criar subcategoria'
                    : 'Erro ao criar categoria'
            )
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className="form-control w-full">
            <label className="label">
                <span className="label-text font-medium">{label}</span>
                {canCreateHere && !creating && (
                    <button
                        type="button"
                        className="btn btn-ghost btn-xs gap-1 text-secondary"
                        onClick={() => setCreating(true)}
                    >
                        <MdAdd className="text-base" />
                        {isSubcategory ? 'Nova subcategoria' : 'Nova categoria'}
                    </button>
                )}
            </label>

            {creating ? (
                <div className="flex flex-col gap-2 sm:flex-row">
                    <input
                        type="text"
                        className="input input-bordered w-full"
                        placeholder={
                            isSubcategory
                                ? 'Nome da nova subcategoria'
                                : 'Nome da nova categoria'
                        }
                        value={newName}
                        autoFocus
                        disabled={saving}
                        onChange={e => setNewName(e.target.value)}
                        onKeyDown={e => {
                            // Enter aqui não pode enviar o formulário do catálogo.
                            if (e.key === 'Enter') {
                                e.preventDefault()
                                void handleCreate()
                            } else if (e.key === 'Escape') {
                                e.preventDefault()
                                closeCreate()
                            }
                        }}
                    />
                    <div className="flex gap-2">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            disabled={saving || !newName.trim()}
                            onClick={() => void handleCreate()}
                        >
                            {saving ? (
                                <span className="loading loading-spinner loading-sm" />
                            ) : (
                                'Criar'
                            )}
                        </button>
                        <button
                            type="button"
                            className="btn btn-ghost"
                            disabled={saving}
                            onClick={closeCreate}
                        >
                            Cancelar
                        </button>
                    </div>
                </div>
            ) : (
                <select
                    className="select select-bordered w-full"
                    value={value ?? ''}
                    disabled={disabled}
                    onChange={e =>
                        onChange(
                            e.target.value ? parseInt(e.target.value, 10) : null
                        )
                    }
                >
                    <option value="">
                        {isSubcategory && !parentId
                            ? 'Escolha a categoria primeiro'
                            : loading
                              ? 'Carregando...'
                              : 'Nenhuma'}
                    </option>
                    {options.map(category => (
                        <option key={category.id} value={category.id}>
                            {category.name}
                        </option>
                    ))}
                </select>
            )}
        </div>
    )
}
